import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import * as types from "../../constants/ComponentConstants";
import { TochtRedenVanStatus } from "../../constants/DomainConstants";
import { formatValidatieStatussesForBeads } from "./NavItemHelper";
import Tooltip from "../common/Tooltip";
import Button from "../common/Button";
import Confirm from "../common/Confirm";
import TabNavIcon from "../common/TabNavIcon";
import Table from "../common/Table";
import { orderByPropertyName } from "../../util/orderBy";
import capitalize from "../../util/stringFormatter";
import conflictApi from "../../api/e-loket/conflictBeoordelingApi";

function isLusInConflict(lusStatus, lussenMetGpxConflict) {
	return !!(lussenMetGpxConflict && lussenMetGpxConflict.some(id => id.toLowerCase() === (lusStatus.entiteitGuid || "").toLowerCase()));
}

const Beads = (props) => {

	const { items, tochtId, confirmStatusPopup, onClickStatusButton, showStatusPopup, redenVanStatus } = props;

	const [lussenMetGpxConflict, setLussenMetGpxConflict] = useState([]);

	useEffect(() => {
		if (tochtId) {
			conflictApi.getLussenMetConflict(tochtId)
				.then((response) => {
					setLussenMetGpxConflict(response.data || []);
				})
				.catch(() => {
					setLussenMetGpxConflict([]);
				});
		}
	}, [tochtId]);

	const tabOrder = getTabOrder();
	let sortedItems = [];
	const lusTypePlural = types.AFSTANDEN_TEKST;

	const hasOnvolledig = items.filter(x => x.volledig != 1).length > 0;
	const lusItems = items.filter(x => x.tab === 2);
	const hasLusConflict = lusItems.some(lus => isLusInConflict(lus, lussenMetGpxConflict));

	const isDoorgestuurdTerGoedkeuring = redenVanStatus === TochtRedenVanStatus.DoorgestuurdTerGoedkeuring;

	let statusSummaryText;
	let statusColor;
	let statusTooltipText = "Nadat jouw dossier volledig ingevuld is en er geen conflicten gedetecteerd zijn met routes van andere tochten wordt het dossier naar de betrokken instanties doorgestuurd ter goedkeuring.";
	if (isDoorgestuurdTerGoedkeuring) {
		statusSummaryText = "Jouw dossier werd ingediend.";
		statusTooltipText = "De betrokken instanties kunnen nu jouw dossier beoordelen. Check op het tabblad ‘Goedkeuringen’ welke instanties jouw dossier reeds beoordeeld hebben.";
		statusColor = types.COLOR_GREEN;
	}
	else if (!hasOnvolledig && !hasLusConflict) {
		statusSummaryText = "Je dossier is volledig. Vanaf 1 jaar voor je evenement wordt dit automatisch doorgestuurd naar de betrokken instanties.";
		statusTooltipText = null;
		statusColor = types.COLOR_GREEN;
	} else if (!hasOnvolledig && hasLusConflict) {
		statusSummaryText = `Eén of meerdere ${lusTypePlural} zijn betrokken in een conflict.`;
		statusColor = types.COLOR_ORANGE;
	} else if (hasOnvolledig && !hasLusConflict) {
		statusSummaryText = "Jouw dossier is nog niet volledig. Vul aan en bewaar steeds ieder tabblad.";
		statusColor = types.COLOR_RED;
	} else {
		statusSummaryText = `Eén of meerdere ${lusTypePlural} zijn betrokken in een conflict en jouw dossier is nog niet volledig. Los de conflicten op, vul aan en bewaar steeds ieder tabblad.`;
		statusColor = types.COLOR_RED;
	}

	let svgcontent = {
		[0]: types.ICON_ORGANISATOR,
		[1]: types.ICON_VELDTOERTOCHT,
		[2]: types.ICON_AFSTANDEN,
		[3]: types.ICON_PUBLICATIE
	};
	let iconColors = {
		[0]: types.COLOR_ORANGE,
		[1]: types.COLOR_TURQUOISE_DARK,
		[2]: types.COLOR_PINK,
		[3]: types.COLOR_BLUE
	};
	let statusTitels = {
		[0]: "Organisator",
		[1]: "Tocht",
		[2]: capitalize(types.AFSTANDEN_TEKST),
		[3]: "Publicatie",
	};
	if (items) {
		tabOrder.forEach((value) => {
			let filteredItems = items.filter(x => x.tab === value);
			sortedItems = orderByPropertyName(orderByPropertyName(sortedItems.concat(filteredItems), "title"), "tab");
		});
	}
	let lusStatusses = sortedItems.filter(s => s.tab == 2);
	const lussenAllVolledig = lusStatusses.filter(s => !s.volledig).length == 0;
	const lussenHaveConflict = lusStatusses.some(s => isLusInConflict(s, lussenMetGpxConflict));
	let statussesVolledig = items && sortedItems.length > 0 ? {
		[0]: sortedItems.find(s => s.tab == 0).volledig,
		[1]: sortedItems.find(s => s.tab == 1).volledig,
		[2]: lussenAllVolledig && !lussenHaveConflict,
		[3]: sortedItems.find(s => s.tab == 3) ? sortedItems.find(s => s.tab == 3).volledig : false
	} : null;

	function getLusTabSummaryText() {
		if (lussenAllVolledig && !lussenHaveConflict) return { text: "Volledig", color: types.COLOR_GREEN };
		if (lussenAllVolledig && lussenHaveConflict) return { text: "Conflict - Volledig", color: types.COLOR_ORANGE };
		if (!lussenAllVolledig && lussenHaveConflict) return { text: "Conflict - Nog niet volledig", color: types.COLOR_ORANGE };
		return { text: "Nog niet volledig", color: types.COLOR_ORANGE };
	}

	function getLusDetailStatus(lusStatus) {
		const inConflict = isLusInConflict(lusStatus, lussenMetGpxConflict);
		const volledig = !!lusStatus.volledig;
		if (volledig && !inConflict) return { text: "Volledig", color: types.COLOR_GREEN };
		if (volledig && inConflict) return { text: "Conflict - Volledig", color: types.COLOR_ORANGE };
		if (!volledig && inConflict) return { text: "Conflict - Nog niet volledig", color: types.COLOR_ORANGE };
		return { text: "Nog niet volledig", color: types.COLOR_ORANGE };
	}

	let lusStatussesTable = lusStatusses && lusStatusses.length > 0 ? lusStatusses.map((lusStatus, i) => {
		const detail = getLusDetailStatus(lusStatus);
		return <Table key={i} type="no-border" style={{ marginBottom: '0px' }}><tr>
			<td style={{ width: '75%' }}>{lusStatus.title}</td><td style={{ width: '25%' }}><span style={{ color: detail.color }}>{detail.text}</span></td>
		</tr></Table>;
	}) : null;
	let lusIndex = 0;

	let statusTable = items && sortedItems.length > 0 ? sortedItems.map((tabStatus, i) => {
		if(tabStatus.tab === 2){
			const lusTabSummary = getLusTabSummaryText();
			const lussenRow =
				<tr key={i}>
					<td style={{ width: '10%' }}>{lusStatusses[0] === tabStatus ? <TabNavIcon type={svgcontent[tabStatus.tab]} color={iconColors[tabStatus.tab]} /> : null}</td>
					<td style={{ width: '10%' }}>{lusStatusses[0] === tabStatus ? statusTitels[tabStatus.tab] : ""}</td>
					<td style={{ width: '15%' }}>{lusStatusses[0] === tabStatus ? <span style={{ color: lusTabSummary.color }}>{lusTabSummary.text}</span> : null}</td>
					<td style={{ width: '65%' }}>
						{lusStatussesTable[lusIndex]}
					</td>
				</tr>;
			lusIndex++;
			return lussenRow;
		}

		return <tr key={i}>
			<td style={{ width: '10%' }}>{<TabNavIcon type={svgcontent[tabStatus.tab]} color={iconColors[tabStatus.tab]} />}</td>
			<td style={{ width: '10%' }}>{statusTitels[tabStatus.tab]}</td>
			<td style={{ width: '15%' }}>{<span style={{ color: statussesVolledig[tabStatus.tab] ? types.COLOR_GREEN : types.COLOR_ORANGE }}>{statussesVolledig[tabStatus.tab] ? "Volledig" : "Nog niet volledig"}</span>}</td>
			<td style={{ width: '65%' }}>
				{(tabStatus.tab === 1 && !statussesVolledig[tabStatus.tab]) ? <span style={{ color: types.COLOR_ORANGE }}>De tab tocht is pas volledig als de gegevens van alle {lusTypePlural} volledig werden ingevuld.</span> : null}
			</td>
		</tr>;
	}) : null;

	const statusDetailConfirmHeight = (570 + ((lusStatusses && lusStatusses.length > 0) ? 100 + ((lusStatusses.length - 1) * 60) : 0)) + 'px';

	return (items && items.length > 0 ?
		(
			<div className="c-beads-wrapper">
				<div className="c-beads-left">
					{!showStatusPopup || <Confirm title="Statusdetail van jouw tocht" showOnTop hideCancel message="Als al deze tabbladen op volledig staan, wordt je dossier automatisch gecontroleerd en ingediend." children={<Table type="no-border">{statusTable}</Table>} callback={confirmStatusPopup} customStyle={{ position: 'relative', padding: '20px', height: statusDetailConfirmHeight, 'overflow-y': 'scroll' }} />}
					<span style={{ color: statusColor }}>{statusSummaryText}</span>
					{statusTooltipText && <Tooltip
						text={statusTooltipText}
						tooltipType={types.TOOLTIP_INFO}
						windowWidth={30}
						tooltipColor={statusColor}
						iconColor={statusColor}
					/>}
					<div className="u-clear" style={{ whiteSpace: 'nowrap' }}><Button onClick={onClickStatusButton} text="Toon statusdetail" className="c-button--left" style={{ verticalAlign: 'baseline', textAlign: 'center', width: 'auto', padding: '7px', marginLeft: '5%', marginRight: '5%' }} /></div>
				</div>
			</div>
		) : null
	);
};

function getTabOrder() {
	return [0, 1, 2, 3, 4, 5];
}

Beads.propTypes = {
	items: PropTypes.array,
	tochtId: PropTypes.string,
	confirmStatusPopup: PropTypes.func.isRequired,
	onClickStatusButton: PropTypes.func.isRequired,
	showStatusPopup: PropTypes.bool,
	redenVanStatus: PropTypes.number
};

function mapStateToProps(state, ownProps) {
	const validatieStatussen = state.validatieStatussen;
	const tochtId = validatieStatussen.length > 0 ? validatieStatussen[0].tochtGuid : null;
	return {
		items: formatValidatieStatussesForBeads(validatieStatussen),
		tochtId
	};
}

export default connect(mapStateToProps)(Beads);
